import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UserApplications} from '../../../../model/user';
import {translate} from '@ngneat/transloco';
import {MenuItem, TreeDragDropService} from 'primeng/api';
import {Menu} from 'primeng/menu';
import {AuthGuard} from '../../../../common/auth-guard.service';

@Component({
  selector: 'app-users-form-applications',
  templateUrl: './users-form-applications.component.html',
  styleUrls: ['./users-form-applications.component.scss'],
  providers: [TreeDragDropService]
})
export class UsersFormApplicationsComponent implements OnInit {

  @Input() userApplications: UserApplications;
  @Output() userApplicationsChange = new EventEmitter<UserApplications>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();

  available: MenuItem[];
  superadmin: MenuItem[];
  admin: MenuItem[];
  viewer: MenuItem[];
  operator: MenuItem[];

  contextMenuAvailable: MenuItem[];
  contextMenuSuperadmin: MenuItem[];
  contextMenuAdmin: MenuItem[];
  contextMenuViewer: MenuItem[];
  contextMenuOperator: MenuItem[];

  draggedApp: MenuItem;

  constructor(
    private authGuard: AuthGuard
  ) {
  }

  ngOnInit(): void {
    this.available = [];
    this.superadmin = [];
    this.admin = [];
    this.viewer = [];
    this.operator = [];

    this.buildContextMenus();

    this.createForm(this.userApplications);
  }

  onPreviousStep(): void {
    this.previousStep.emit();
    this.updateApplicationsState();
    this.userApplicationsChange.emit(this.userApplications);
  }

  onNextStep(): void {
    this.nextStep.emit();
    this.updateApplicationsState();
    this.userApplicationsChange.emit(this.userApplications);
  }

  public dragStart(app: MenuItem): void {
    this.draggedApp = app;
  }

  public drop(target: MenuItem[]): void {
    if (this.draggedApp) {
      const removed = this.removeDraggedAppFromSourceCollection();
      if (removed) {
        target.push(this.draggedApp);
        this.draggedApp = null;
      }
    }
  }

  public dragEnd(): void {
    this.draggedApp = null;
  }

  public touchStart(app: MenuItem, menu: Menu, event: any): void {
    const {currentTarget} = event;
    this.draggedApp = app;
    menu.hide();
    setTimeout(() => menu.show({currentTarget}));
  }

  private move(source: MenuItem[], target: MenuItem[]): void {
    const index = source.indexOf(this.draggedApp);
    source.splice(index, 1);
    target.push(this.draggedApp);
    this.draggedApp = null;
  }

  private createForm(user: UserApplications): void {
    user.available.forEach(application => this.available.push({label: application, icon: 'pi pi-desktop'}));
    user.superadmin.forEach(application => this.superadmin.push({label: application, icon: 'pi pi-desktop'}));
    user.admin.forEach(application => this.admin.push({label: application, icon: 'pi pi-desktop'}));
    user.viewer.forEach(application => this.viewer.push({label: application, icon: 'pi pi-desktop'}));
    user.operator.forEach(application => this.operator.push({label: application, icon: 'pi pi-desktop'}));
  }

  private updateApplicationsState(): void {
    this.userApplications.viewer = this.viewer.map(element => element.label);
    this.userApplications.operator = this.operator.map(element => element.label);
    this.userApplications.admin = this.admin.map(element => element.label);
    this.userApplications.superadmin = this.superadmin.map(element => element.label);
    this.userApplications.available = this.available.map(element => element.label);
  }

  private removeDraggedAppFromSourceCollection(): boolean {
    if (this.available.indexOf(this.draggedApp) !== -1) {
      this.available = this.available.filter(app => app !== this.draggedApp);
      return true;
    }
    if (this.superadmin.indexOf(this.draggedApp) !== -1) {
      this.superadmin = this.superadmin.filter(app => app !== this.draggedApp);
      return true;
    }
    if (this.admin.indexOf(this.draggedApp) !== -1) {
      this.admin = this.admin.filter(app => app !== this.draggedApp);
      return true;
    }
    if (this.viewer.indexOf(this.draggedApp) !== -1) {
      this.viewer = this.viewer.filter(app => app !== this.draggedApp);
      return true;
    }
    if (this.operator.indexOf(this.draggedApp) !== -1) {
      this.operator = this.operator.filter(app => app !== this.draggedApp);
      return true;
    }
    return false;
  }

  private buildContextMenus(): void {
    this.contextMenuAvailable = [];
    this.contextMenuAvailable.push({
      label: translate('misc.roleChange.allowAccess', {role: translate('role.viewer')}),
      icon: 'pi pi-check-circle',
      command: (event) => this.move(this.available, this.viewer)
    });
    this.contextMenuAvailable.push({
      label: translate('misc.roleChange.allowAccess', {role: translate('role.operator')}),
      icon: 'pi pi-check-circle',
      command: (event) => this.move(this.available, this.operator)
    });
    this.contextMenuAvailable.push({
      label: translate('misc.roleChange.allowAccess', {role: translate('role.admin')}),
      icon: 'pi pi-check-circle',
      command: (event) => this.move(this.available, this.admin)
    });
    if (this.authGuard.superAdminRequired()) {
      this.contextMenuAvailable.push({
        label: translate('misc.roleChange.allowAccess', {role: translate('role.superAdmin')}),
        icon: 'pi pi-check-circle',
        command: (event) => this.move(this.available, this.superadmin)
      });
    }


    this.contextMenuSuperadmin = [];
    this.contextMenuSuperadmin.push({
      label: translate('misc.roleChange.degrade', {role: translate('role.admin')}),
      icon: 'pi pi-angle-down',
      command: (event) => this.move(this.superadmin, this.admin)
    });
    this.contextMenuSuperadmin.push({
      label: translate('misc.roleChange.degrade', {role: translate('role.viewer')}),
      icon: 'pi pi-angle-double-down',
      command: (event) => this.move(this.superadmin, this.viewer)
    });
    this.contextMenuSuperadmin.push({
      label: translate('misc.roleChange.degrade', {role: translate('role.operator')}),
      icon: 'pi pi-angle-double-down',
      command: (event) => this.move(this.superadmin, this.operator)
    });
    this.contextMenuSuperadmin.push({
      label: translate('misc.roleChange.revokeAccess'),
      icon: 'pi pi-times-circle',
      command: (event) => this.move(this.superadmin, this.available)
    });

    this.contextMenuAdmin = [];
    if (this.authGuard.superAdminRequired()) {
      this.contextMenuAdmin.push({
        label: translate('misc.roleChange.promote', {role: translate('role.superAdmin')})
        , icon: 'pi pi-angle-up',
        command: (event) => this.move(this.admin, this.superadmin)
      });
    }
    this.contextMenuAdmin.push({
      label: translate('misc.roleChange.degrade', {role: translate('role.viewer')}),
      icon: 'pi pi-angle-down',
      command: (event) => this.move(this.admin, this.viewer)
    });
    this.contextMenuAdmin.push({
      label: translate('misc.roleChange.degrade', {role: translate('role.operator')}),
      icon: 'pi pi-angle-down',
      command: (event) => this.move(this.admin, this.operator)
    });
    this.contextMenuAdmin.push({
      label: translate('misc.roleChange.revokeAccess'),
      icon: 'pi pi-times-circle',
      command: (event) => this.move(this.admin, this.available)
    });

    this.contextMenuViewer = [];
    if (this.authGuard.superAdminRequired()) {
      this.contextMenuViewer.push({
        label: translate('misc.roleChange.promote', {role: translate('role.superAdmin')}),
        icon: 'pi pi-angle-double-up',
        command: (event) => this.move(this.viewer, this.superadmin)
      });
    }
    this.contextMenuViewer.push({
      label: translate('misc.roleChange.promote', {role: translate('role.admin')}),
      icon: 'pi pi-angle-up',
      command: (event) => this.move(this.viewer, this.admin)
    });
    this.contextMenuViewer.push({
      label: translate('misc.roleChange.promote', {role: translate('role.operator')}),
      icon: 'pi pi-angle-up',
      command: (event) => this.move(this.viewer, this.operator)
    });
    this.contextMenuViewer.push({
      label: translate('misc.roleChange.revokeAccess'),
      icon: 'pi pi-times-circle',
      command: (event) => this.move(this.viewer, this.available)
    });

    this.contextMenuOperator = [];
    if (this.authGuard.superAdminRequired()) {
      this.contextMenuOperator.push({
        label: translate('misc.roleChange.promote', {role: translate('role.superAdmin')}),
        icon: 'pi pi-angle-double-up',
        command: (event) => this.move(this.operator, this.superadmin)
      });
    }
    this.contextMenuOperator.push({
      label: translate('misc.roleChange.promote', {role: translate('role.admin')}),
      icon: 'pi pi-angle-up',
      command: (event) => this.move(this.operator, this.admin)
    });
    this.contextMenuOperator.push({
      label: translate('misc.roleChange.degrade', {role: translate('role.viewer')}),
      icon: 'pi pi-angle-down',
      command: (event) => this.move(this.operator, this.viewer)
    });
    this.contextMenuOperator.push({
      label: translate('misc.roleChange.revokeAccess'),
      icon: 'pi pi-times-circle',
      command: (event) => this.move(this.operator, this.available)
    });
  }
}
